import type { StudentRoles, UserAdminRoles } from "@/modules/user/api/types"
import type { FunctionComponent } from "react"

import { clsx } from "clsx"
import dynamic from "next/dynamic"
import { usePathname } from "next/navigation"

import { NEW_SIGN_IN_FLOW, NEW_SIGN_IN_FLOW_VARIANTS } from "@/modules/careerCenter/constants/flags"
import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { isWwwCareerCenter } from "@/modules/careerCenter/isWwwCareerCenter"
import { Brand } from "@/modules/headerFooterFO/Brand"
import { isLinkActive } from "@/modules/headerFooterFO/Header/helpers/isLinkActive"
import { LocalesDropdown } from "@/modules/headerFooterFO/LocalesDropdown"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { isChildPage, isFeaturePage } from "@/modules/routing/helpers/isPage"
import { useHref } from "@/modules/routing/hooks/useHref"
import { useFlag } from "@/modules/unleash/hooks/useFlag"
import { STUDENT_ROLES, USER_ADMIN_ROLES } from "@/modules/user/api/types"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

import { DropdownButton } from "./DropdownButton"
import { DropdownUser } from "./DropdownUser"
import { LoginUser } from "./LoginUser"
import { LoginButton } from "./LoginUser/LoginButton"
import { Nav } from "./Nav"

import styles from "./Header.module.css"
import dropdownMenuStyles from "./styles/DropdownMenu.module.css"

const DynamicPersonas = dynamic(() => import("./Personas").then(mod => mod.Personas))

export const Header: FunctionComponent = () => {
  const { t } = useTranslation(["module_header_fo"])
  const href = useHref()
  const pathname = usePathname()
  const { dataCurrentUser } = useCurrentUserFetch()
  const isLogged = !!dataCurrentUser?.isLogged
  const userRole = dataCurrentUser?.attributes.roleTitle
  const locale = useLocale()
  const { dataCareerCenter } = useCareerCenterFetch()
  const isNewSignInFlowEnabled = useFlag(NEW_SIGN_IN_FLOW, NEW_SIGN_IN_FLOW_VARIANTS.ENABLED)

  const isCareerCenter = !isWwwCareerCenter(dataCareerCenter)
  const displayLocales = !isLogged && (!isChildPage(pathname) || isFeaturePage(pathname, "profiles/new"))

  const logoUrl = (): string => {
    const showDashboardPath =
      USER_ADMIN_ROLES.includes(userRole as UserAdminRoles) || STUDENT_ROLES.includes(userRole as StudentRoles)

    if (isLogged && showDashboardPath) {
      return `/${locale}/dashboard`
    }

    return `/${locale}`
  }

  return (
    <header
      className={clsx("js-Header", styles["js-Header-pages"], styles.main, {
        [styles["header--anonymousUser"]]: !isLogged && !isCareerCenter,
      })}
      role="banner"
    >
      <nav role="navigation" className={clsx("js-Header-wrapper", styles.nav)}>
        {/* Logo */}
        <div className={styles.logo}>
          <a
            href={logoUrl()}
            className={styles.link}
            aria-label={t("module_header_fo.home")}
            data-testid="headerHomeLink"
          >
            <Brand from="header" />
          </a>
        </div>

        <div className={clsx("js-Header-menu", styles.dropdown)}>
          <div className={styles.dropdownButton}>
            <DropdownButton ariaLabel={t("module_header_fo.icon_menu")} />
          </div>

          {isNewSignInFlowEnabled && !isLogged && (
            <LoginButton
              careerCenterName={dataCareerCenter?.name || ""}
              className={styles.account}
              isWwwCareerCenter={isWwwCareerCenter(dataCareerCenter)}
              label={t("module_header_fo.login_button.label")}
              uiPosition="header"
            />
          )}
          {isNewSignInFlowEnabled && isLogged && (
            <div className={clsx(dropdownMenuStyles["hdr-DropdownMenu"], styles.account)}>
              <div
                className={clsx(
                  dropdownMenuStyles["hdr-DropdownMenu-item"],
                  dropdownMenuStyles["hdr-DropdownMenu-item--user"],
                  {
                    [dropdownMenuStyles["hdr-DropdownMenu-item--userActive"]]:
                      !!dataCurrentUser?.attributes.isStudent &&
                      isLinkActive(href, `/${locale}/profiles/${dataCurrentUser?.attributes.uuid}`),
                  }
                )}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
              >
                <DropdownUser />
              </div>
            </div>
          )}
          {!isNewSignInFlowEnabled && (
            <div className={clsx(dropdownMenuStyles["hdr-DropdownMenu"], styles.account)}>
              <div
                className={clsx(
                  dropdownMenuStyles["hdr-DropdownMenu-item"],
                  dropdownMenuStyles["hdr-DropdownMenu-item--user"],
                  {
                    [dropdownMenuStyles["hdr-DropdownMenu-item--userActive"]]:
                      !!dataCurrentUser?.attributes.isStudent &&
                      isLinkActive(href, `/${locale}/profiles/${dataCurrentUser?.attributes.uuid}`),
                  }
                )}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
              >
                {isLogged ? <DropdownUser /> : <LoginUser />}
              </div>
            </div>
          )}
        </div>
        {/* TopNav */}
        {!isLogged && !isCareerCenter ? (
          <div className={styles.topNav}>
            <DynamicPersonas />
          </div>
        ) : null}
        {/* Navigation */}
        <div className={styles.primaryNav}>
          <Nav />
        </div>
        {/* Locales */}
        {displayLocales && (
          <div className={styles.language}>
            <LocalesDropdown from="header" />
          </div>
        )}
      </nav>
    </header>
  )
}
